import React, { useState } from 'react';
import { Formik } from 'formik';
import { PriorPolicyChallengeFormValuesType } from 'QuoteApp/services/endpoints/PostChallengePriorPolicy';
import { ReportDetailRow } from 'QuoteApp/components/ReportDetails/ReportDetailRow';
import { ChallengeForm } from 'QuoteApp/components/ReportDetails/ChallengeForm';
import PriorPolicyChallengeForm from 'QuoteApp/components/ReportDetails/PriorPolicyChallengeForm/PriorPolicyChallengeForm';
import { useLazyQuoteApi } from 'Shared/services/Api';
import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers/AppProvider';
import { transformChallengePriorPolicyRequest } from 'QuoteApp/services/endpoints/PostChallengePriorPolicy/request';
import {
  getReportMessages,
  ReportChangeMessageType,
  ReportMessages,
} from 'QuoteApp/screens/Reports/ReportMessages';
import { useEdit } from 'QuoteApp/utils/contexts';
import { getErrorMessage } from 'QuoteApp/utils';
import { useToast } from 'Shared/components';
import styles from './styles.module.css';
import { getValidationSchema } from './PriorPolicyForm';

type AddPriorPolicyType = {
  onCancelAdd: () => void;
  driverId: string;
  onSuccess: () => void;
};

const AddPriorPolicy = ({
  onCancelAdd,
  driverId,
  onSuccess,
}: AddPriorPolicyType) => {
  const { appId, fetchRate, rate, setDetails } = useAppContext();
  const { changeEditingId } = useEdit();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);

  const [, addPriorPolicyData] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/drivers/${driverId}/add_prior_policy`,
  });

  const handleSubmit = async (values: PriorPolicyChallengeFormValuesType) => {
    try {
      const response = await addPriorPolicyData({
        data: values['temp-prior-policy'],
        transformRequest: transformChallengePriorPolicyRequest,
        transformResponse: transformAppDetails,
      });

      if (response.data.status === 'open') {
        const newRate = await fetchRate();
        rate &&
          newRate &&
          setMessages(
            getReportMessages(
              rate,
              newRate,
              'disputed report data',
              'accident-challenging'
            )
          );
      }
      changeEditingId(null);
      onSuccess();
      setDetails(response.data);
    } catch (e) {
      toast({
        type: 'error',
        message: getErrorMessage(e),
        withSecondaryNav: false,
        icon: 'error',
      });
    }
  };

  return (
    <Formik<PriorPolicyChallengeFormValuesType>
      initialValues={{
        'temp-prior-policy': {
          challengeType: 'edit',
          carrierName: '',
          startDate: '',
          endDate: '',
          amBestNumber: '',
          state: '',
          challengeReason: '',
          reasonText: '',
          biLimit: '',
          cslLimit: '',
          pipLimit: '',
          policyNumber: '',
          limits: '',
        },
      }}
      onSubmit={handleSubmit}
      validationSchema={getValidationSchema('temp-prior-policy')}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <>
            <ReportDetailRow
              identifier={'temp-prior-policy'}
              canChallenge={true}
              challengeCommand={undefined}
              source={'user_reported'}
              type="prior-policies"
              shortSource
              checkmarked={null}
              onEdit={() => {
                setFieldValue(`temp-prior-policy.challengeType`, 'edit');
              }}
              challengeForm={
                <ChallengeForm
                  variant={values['temp-prior-policy'].challengeType || 'edit'}
                  identifier={'temp-prior-policy'}
                  type="prior-policies"
                  includeRemove
                  challengeReasonConfig={{}}
                  onRemove={() => {
                    setFieldValue(`temp-prior-policy.challengeType`, 'remove');
                  }}
                  isAdding={true}
                  onCancelAdd={onCancelAdd}
                />
              }
            >
              <PriorPolicyChallengeForm
                identifier={'temp-prior-policy'}
                values={values}
                setFieldValue={setFieldValue}
                isEditing={true}
                errors={errors}
                priorPolicy={null}
                isAdding={true}
              />
            </ReportDetailRow>
            <ReportMessages messages={messages} className={styles.message} />
          </>
        );
      }}
    </Formik>
  );
};

export default AddPriorPolicy;
